/**
 * G3 — defensive parse at L3 memberships client boundary.
 * Square logo via resolveConsumerSquareLogoUrl; rim via normalizeLogoChipRimSettings
 * (defaults when absent/invalid — never strip).
 */

import {
  normalizeLogoChipBackgroundSettings,
  normalizeLogoChipRimSettings,
  resolveConsumerSquareLogoUrl,
} from 'pi-kiosk-shared';
import type {
  CustomerMembershipItemDTO,
  CustomerMembershipsDTO,
} from './accountTypes.js';

const MEMBERSHIP_STATUSES = new Set([
  'ACTIVE',
  'PROSPECT',
  'SUSPENDED',
  'REVOKED',
]);

type MembershipStatus = CustomerMembershipItemDTO['membershipStatus'];

function isMembershipStatus(value: unknown): value is MembershipStatus {
  return typeof value === 'string' && MEMBERSHIP_STATUSES.has(value);
}

/** Normalize one memberships row — null when required identity fields are invalid. */
export function normalizeCustomerMembershipItem(
  raw: unknown,
): CustomerMembershipItemDTO | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const item = raw as Record<string, unknown>;
  const tenantId = item.tenantId;
  const tenantCode = item.tenantCode;
  const membershipStatus = item.membershipStatus;
  const isCurrentTenant = item.isCurrentTenant;
  if (
    typeof tenantId !== 'number' ||
    typeof tenantCode !== 'string' ||
    !isMembershipStatus(membershipStatus) ||
    typeof isCurrentTenant !== 'boolean'
  ) {
    return null;
  }
  let tenantName: string | null = null;
  if (typeof item.tenantName === 'string') {
    tenantName = item.tenantName;
  } else if (item.tenantName === null) {
    tenantName = null;
  }
  const rim = normalizeLogoChipRimSettings(item);
  const background = normalizeLogoChipBackgroundSettings(item);
  const status = item.status;
  return {
    tenantId,
    tenantCode,
    tenantName,
    membershipStatus,
    ...(isMembershipStatus(status) ? { status } : {}),
    isCurrentTenant,
    logoUrl: resolveConsumerSquareLogoUrl(item.logoUrl),
    logoUrlDark: resolveConsumerSquareLogoUrl(item.logoUrlDark),
    ...rim,
    ...background,
  };
}

/** Normalize memberships envelope — filters invalid rows; preserves sibling flags. */
export function normalizeCustomerMembershipsDTO(
  data: unknown,
): CustomerMembershipsDTO {
  const raw =
    data !== null && typeof data === 'object'
      ? (data as CustomerMembershipsDTO)
      : null;
  const itemsRaw = Array.isArray(raw?.memberships) ? raw.memberships : [];
  const memberships: CustomerMembershipItemDTO[] = [];
  for (const row of itemsRaw) {
    const normalized = normalizeCustomerMembershipItem(row);
    if (normalized !== null) {
      memberships.push(normalized);
    }
  }
  if (raw === null) {
    return { memberships };
  }
  return { ...raw, memberships };
}
